import React from "react";
import { motion } from "framer-motion";

const timelineEvents = [
  {
    year: "2015 – Present",
    role: "Aqua Aerobics Instructor / Swimming Coach",
    place: "Freelance / Various Centers",
    description:
      "Energizing bodies and minds through fun, low-impact Aqua Aerobics — fitness that flows with the water.",
  },
  {
    year: "Jan 2023 – Dec 2024",
    role: "Clinical Dietitian",
    place: "The Yoga Institute",
    description:
      "Integrated traditional yogic principles with evidence-based medical nutrition therapy.",
  },
  {
    year: "Dec 2020 – Dec 2022",
    role: "Clinical Dietitian",
    place: "Hinduja Hospital, Mahim",
    description:
      "Managed diverse inpatient & outpatient cases—metabolic disorders, renal, GI, post-op recovery.",
  },
  {
    year: "Oct 2020 – Dec 2020",
    role: "Junior Dietitian",
    place: "D.Y. Patil Hospital",
    description:
      "Early clinical rotation focusing on patient counseling & chart-based nutritional assessments.",
  },
  {
    year: "2018 – 2020",
    role: "Swimming Coach",
    place: "Sports Clubs / Academies",
    description:
      "Guiding swimmers to master technique, build endurance, and find wellness through water.",
  },
  {
    year: "2019",
    role: "Intern Dietitian",
    place: "Clinical Internship",
    description:
      "Gained foundational experience in clinical nutrition and patient care.",
  },
];

const About = () => {
  return (
    <section id="about" className="py-32 bg-muted/20">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
          {/* Header / Intro */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="sticky top-32"
            >
              <span className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-4 block">
                About Me
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-6 leading-tight">
                My Journey
              </h2>
              <p className="text-lg text-muted-foreground font-light leading-relaxed mb-8">
                A path dedicated to understanding the human body and the power
                of nutrition. My experience spans clinical care, holistic
                wellness, and patient advocacy.
              </p>
              <div className="h-1 w-20 bg-primary/30 rounded-full" />
            </motion.div>
          </div>

          {/* Timeline */}
          <div className="lg:col-span-8">
            <div className="relative border-l border-border/50 ml-4 md:ml-0 space-y-16">
              {timelineEvents.map((event, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true, margin: "-50px" }}
                  transition={{ delay: index * 0.1, duration: 0.6 }}
                  className="relative pl-8 md:pl-16 group"
                >
                  {/* Timeline Dot */}
                  <span className="absolute top-8 left-[-5px] w-2.5 h-2.5 rounded-full bg-muted-foreground/30 ring-4 ring-background group-hover:bg-primary transition-colors duration-500 z-10" />

                  <div className="p-6 -ml-6 rounded-2xl transition-all duration-300 hover:bg-white/40 hover:backdrop-blur-md hover:shadow-sm hover:border hover:border-white/20">
                    <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between mb-3">
                      <h3 className="text-2xl font-serif font-semibold text-foreground group-hover:text-primary transition-colors duration-300">
                        {event.role}
                      </h3>
                      <span className="text-sm font-medium text-muted-foreground/80 mt-1 sm:mt-0 font-mono tracking-wide">
                        {event.year}
                      </span>
                    </div>

                    <h4 className="font-semibold text-foreground/80 mb-3 uppercase tracking-wide text-xs">
                      {event.place}
                    </h4>
                    <p className="text-muted-foreground font-light leading-relaxed max-w-2xl">
                      {event.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;
